if (!customElements.get('media-horizontal-scroll')) {
  customElements.define('media-horizontal-scroll', class MediaHorizontalScroll extends HTMLElement {
    constructor() {
      super();
      this.curr = 0;
      this.target = 0;
      this.raf = null;
      this.isIntersecting = false;
      this.elementOffsetTop = 0;
      this.isMobileDevice = false;
      this.originalItems = [];
      this.isInitialized = false;
      this.isDragging = false;
      this.startX = 0;
      this.dragOffset = 0;
      this.startDragOffset = 0;
      this.resizer = null;
      this.lastWidth = 0;
      this.winH = window.innerHeight;
      this.lastWinWidth = window.innerWidth;
      this.lastTrackTransform = '';
      this.lastScaleTransform = '';
      this.lastWillChange = '';
      this.lastScrollY = -1;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);
      this.isPressed = false;
      this.didDrag = false;
      this.onDragStart = this.onDragStart.bind(this);
      this.updateValues = this.updateValues.bind(this);
      this.boundAnimate = this.animate.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.boundLazyReleaseCheck = this.checkLazyRelease.bind(this);
      this.resizeRaf = null;
      this.refreshTimer = null;

      this.parentShopifySection = this.closest('.shopify-section');
      this.preloadNextSection();
    }

    connectedCallback() {
      requestAnimationFrame(() => this.init());
    }

    init() {
      this.scaleLayer = this.querySelector('.media-hor-scroll__scale');
      this.track = this.querySelector('.media-hor-scroll__track');

      if (!this.scaleLayer || !this.track) return;

      if (this.originalItems.length === 0) {
        this.originalItems = Array.from(this.track.children);
      }

      this.baseSpeed = parseFloat(this.getAttribute('data-speed')) / 50 || 0.5;
      this.ease = parseFloat(this.getAttribute('data-ease')) || 0.08;
      const scaleAttr = parseFloat(this.getAttribute('data-scale'));
      this.scaleAmount = Number.isFinite(scaleAttr) ? scaleAttr / 100 : 0.5;
      this.isReverse = this.hasAttribute('data-reverse');

      this.scaleLayer.style.transformOrigin = this.isReverse ? 'right bottom' : 'left bottom';

      this.isMobileDevice = DeviceUtils.isMobileSmall;

      this.preloadOwnImages();

      this.waitForLayout().then(() => {
        if (!this.isInitialized) {
          this.initDragEvents();
          this.initObserver();

          window.addEventListener('scroll', this.onScroll, { passive: true });

          window.addEventListener('resize', this.onWindowResize, { passive: true });

          this.resizer = new ResizeObserver((entries) => {
            for (let entry of entries) {
              const newWidth = Math.round(entry.contentRect.width);
              if (this.lastWidth !== newWidth) {
                const isFirst = this.lastWidth === 0;
                this.lastWidth = newWidth;
                this.isMobileDevice = window.DeviceUtils ? DeviceUtils.isMobileSmall : window.innerWidth < 577;

                if (isFirst) {
                  this.refresh();
                } else {
                  this.scheduleRefresh();
                }
              }
            }
          });
          this.resizer.observe(this);
          this.isInitialized = true;
          customElements.get('lazy-section-template')?.hydrateUpcoming(this.parentShopifySection);
          this.scheduleLazyHold();
        }
      });
    }

    preloadOwnImages() {
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    scheduleLazyHold() {
      const hold = () => {
        if (this.lazyHoldDone || this.lazyHeld) return;
        if (this.parentShopifySection.getBoundingClientRect().bottom <= 0) {
          this.lazyHoldDone = true;
          return;
        }
        this.holdLazyImages();
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(hold, { timeout: 1000 });
      } else {
        setTimeout(hold, 200);
      }
    }

    holdLazyImages() {
      if (this.lazyHeld || this.lazyHoldDone) return;
      this.lazyHeld = true;
      customElements.get('lazy-section-template')?.holdLazyAfter(this.parentShopifySection);
      window.addEventListener('scroll', this.boundLazyReleaseCheck, { passive: true });
    }

    releaseLazyImages() {
      if (!this.lazyHeld) return;
      this.lazyHeld = false;
      customElements.get('lazy-section-template')?.releaseLazyAfter(this.parentShopifySection);
      window.removeEventListener('scroll', this.boundLazyReleaseCheck);
      clearTimeout(this.lazyReleaseTimer);
    }

    checkLazyRelease() {
      clearTimeout(this.lazyReleaseTimer);
      this.lazyReleaseTimer = setTimeout(() => {
        if (!this.lazyHeld || this.isIntersecting) return;
        if (this.parentShopifySection.getBoundingClientRect().bottom <= 0) this.finishLazyHold();
      }, 150);
    }

    finishLazyHold() {
      this.lazyHoldDone = true;
      this.releaseLazyImages();
    }

    preloadNextSection() {
      if (!this.parentShopifySection) return;

      const nextSection = this.parentShopifySection.nextElementSibling;
      if (!nextSection || !nextSection.classList.contains('shopify-section') || nextSection.dataset.preloaded) return;

      nextSection.dataset.preloaded = 'true';

      const lazySection = nextSection.querySelector('lazy-section-template');
      if (lazySection && !lazySection.hydrated && typeof lazySection.hydrate === 'function') {
        lazySection.hydrate('forced_preload_from_media_scroll');
        return;
      }

      nextSection.querySelectorAll('img').forEach(img => {
        if (img.loading === 'lazy') img.loading = 'eager';
      });
    }

    updateValues() {
      this.winH = window.innerHeight;
      this.calc();
      this.wake();
    }

    onWindowResize() {
      if (window.innerWidth === this.lastWinWidth || this.resizeRaf) return;

      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;

        if (window.innerWidth !== this.lastWinWidth) {
          this.lastWinWidth = window.innerWidth;
          this.updateValues();
        }
      });
    }

    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 150);
    }

    refresh() {
      this.dragOffset = 0;
      this.clearClones();

      requestAnimationFrame(() => {
        this.calc();
        this.setupClones();
        this.updateSpeed();
        this.setInitialPosition();
        this.wake();
      });
    }

    clearClones() {
      this.track.innerHTML = '';
      this.originalItems.forEach(item => {
        item.style.width = '';
        this.track.appendChild(item);
      });
    }

    onPointerDown(e) {
      this.isPressed = true;
      this.didDrag = false;
      this.startX = e.clientX;
      this.startDragOffset = this.dragOffset;
    }

    onPointerMove(e) {
      if (!this.isPressed) return;

      if (!this.isDragging) {
        if (Math.abs(e.clientX - this.startX) < 5) return;

        this.isDragging = true;
        this.didDrag = true;
        this.track.setPointerCapture(e.pointerId);
      }

      const x = e.clientX;
      const dist = (x - this.startX) * 1.5;

      let nextOffset = this.isReverse ? (this.startDragOffset + dist) : (this.startDragOffset - dist);

      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;
      const scrollBase = relativeScroll * this.speed;

      const minAllowedOffset = -scrollBase;
      const maxAllowedOffset = this.maxScroll - scrollBase;

      this.dragOffset = Math.max(minAllowedOffset, Math.min(maxAllowedOffset, nextOffset));
      this.wake();
    }

    onPointerUp(e) {
      this.isPressed = false;
      this.isDragging = false;
      this.wake();
      if (e && e.pointerId && this.track.hasPointerCapture(e.pointerId)) {
        this.track.releasePointerCapture(e.pointerId);
      }
    }

    onClick(e) {
      if (!this.didDrag) return;

      this.didDrag = false;
      e.preventDefault();
      e.stopPropagation();
    }

    onDragStart(e) {
      e.preventDefault();
    }

    initDragEvents() {
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onClick, true);
    }

    waitForLayout() {
      return new Promise((resolve) => {
        const check = () => {
          if (this.originalItems.length > 0 && this.originalItems[0].offsetWidth > 0) {
            resolve();
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      });
    }

    setupClones() {
      if (this.originalItems.length === 0) return;

      if (this.isMobileDevice) {
        const viewportWidth = window.innerWidth;
        this.contentWidth = this.track.scrollWidth;
        this.maxScroll = this.getMaxScroll(this.contentWidth, viewportWidth);
        return;
      }

      const viewportWidth = window.innerWidth;
      const trackStyles = window.getComputedStyle(this.track);
      const gap = parseFloat(trackStyles.gap) || 0;

      let setWidth = 0;
      const originalWidths = this.originalItems.map(item => {
        const w = item.offsetWidth;
        setWidth += w + gap;
        return w;
      });

      if (setWidth <= 0) return;

      const cycleWidth = (i) => originalWidths[i % originalWidths.length] + gap;
      const maxClones = this.originalItems.length * 10;

      const fillWidth = this.getVisibleWidth(viewportWidth) * 1.25;

      let cloneCount = 0;
      let contentWidth = setWidth;

      while (contentWidth <= fillWidth && cloneCount < maxClones) {
        contentWidth += cycleWidth(cloneCount);
        cloneCount++;
      }

      const extraClones = this.getExtraClones(this.originalItems.length);

      for (let i = 0; i < extraClones && cloneCount < maxClones; i++) {
        contentWidth += cycleWidth(cloneCount);
        cloneCount++;
      }

      if (cloneCount < maxClones) {
        contentWidth += cycleWidth(cloneCount);
        cloneCount++;
      }

      const travelWidth = contentWidth;

      if (cloneCount < maxClones) {
        contentWidth += cycleWidth(cloneCount);
        cloneCount++;
      }

      for (let i = 0; i < cloneCount; i++) {
        const index = i % this.originalItems.length;
        const clone = this.originalItems[index].cloneNode(true);
        clone.style.width = originalWidths[index] + 'px';
        this.track.appendChild(clone);
      }

      const leadIn = this.originalItems[0].offsetLeft;

      this.contentWidth = contentWidth;
      this.maxScroll = this.getMaxScroll(travelWidth + leadIn, viewportWidth);
    }

    getVisibleWidth(viewportWidth) {
      const minScale = this.isMobileDevice ? 1 : Math.max(0.1, 1 - this.scaleAmount);

      return viewportWidth / minScale;
    }

    getMaxScroll(contentWidth, viewportWidth) {
      return Math.max(0, contentWidth - this.getVisibleWidth(viewportWidth));
    }

    getTrackOffset() {
      if (!this.isReverse) return -this.curr;

      const viewportWidth = window.innerWidth;
      const scaleOverflow = this.getVisibleWidth(viewportWidth) - viewportWidth;

      return this.curr - this.maxScroll - scaleOverflow;
    }

    getExtraClones(total) {
      const speed = parseFloat(this.getAttribute('data-speed'));

      if (speed >= 30) return Math.max(2, Math.round(total * 0.5));
      if (speed >= 15) return Math.max(1, Math.round(total / 3));
      return 0;
    }

    calc() {
      const rect = this.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      this.elementOffsetTop = rect.top + scrollY;
      this.elementHeight = rect.height;
      this.updateSpeed();
      if (this.isMobileDevice) {
        this.setScaleTransform('scale(1)');
      }
    }

    setTrackTransform(value) {
      if (this.lastTrackTransform === value) return;
      this.track.style.transform = value;
      this.lastTrackTransform = value;
    }

    setScaleTransform(value) {
      if (this.lastScaleTransform === value) return;
      this.scaleLayer.style.transform = value;
      this.lastScaleTransform = value;
    }

    parkAt(value) {
      this.dragOffset = 0;
      this.curr = value;
      this.target = value;
      const xPos = this.getTrackOffset();
      this.setTrackTransform(`translate3d(${xPos.toFixed(2)}px, 0, 0)`);
    }

    setWillChange(value) {
      if (this.lastWillChange === value) return;
      this.track.style.willChange = value;
      this.scaleLayer.style.willChange = value;
      this.lastWillChange = value;
    }

    updateSpeed() {
      const travel = this.winH + this.elementHeight;
      const distance = this.maxScroll || 0;
      const needed = travel > 0 ? distance / travel : 0;
      const base = this.isMobileDevice ? this.baseSpeed * 2.5 : this.baseSpeed;

      this.speed = Math.max(base, needed);
    }

    setInitialPosition() {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;

      this.target = Math.max(0, Math.min(this.maxScroll, (relativeScroll * this.speed) + this.dragOffset));
      this.curr = this.target;

      const xPos = this.getTrackOffset();
      this.setTrackTransform(`translate3d(${xPos.toFixed(4)}px, 0, 0)`);
    }

    initObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isIntersecting = entry.isIntersecting;
          if (this.isIntersecting) {
            this.holdLazyImages();
            this.setWillChange('transform');
            this.calc();
            const scrollY = window.pageYOffset || document.documentElement.scrollTop;
            const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;
            this.target = Math.max(0, Math.min(this.maxScroll, (relativeScroll * this.speed) + this.dragOffset));
            this.curr = this.target;

            if (!this.raf) this.animate();
          } else {
            if (entry.boundingClientRect.bottom <= 0) this.finishLazyHold();
            if (this.raf) {
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }
            this.setWillChange('auto');

            const rect = entry.boundingClientRect;
            if (rect.bottom <= 0) {
              this.parkAt(this.maxScroll);
            } else if (rect.top >= this.winH) {
              this.parkAt(0);
            }
          }
        });
      }, { rootMargin: '10% 0px' });
      this.observer.observe(this);
    }

    animate() {
      if (!this.isIntersecting) return;

      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;

      this.target = Math.max(0, Math.min(this.maxScroll, (relativeScroll * this.speed) + this.dragOffset));

      const diff = this.target - this.curr;

      if (Math.abs(diff) > 0.1) {
        const atEnd = this.target >= this.maxScroll;
        const baseEase = this.isMobileDevice ? 0.12 : this.ease;
        const currentEase = atEnd ? Math.max(baseEase, 0.25) : baseEase;
        this.curr += diff * currentEase;
      } else {
        this.curr = this.target;
      }

      const xPos = this.getTrackOffset();
      this.setTrackTransform(`translate3d(${xPos.toFixed(2)}px, 0, 0)`);

      if (!this.isMobileDevice) {
        const totalDistance = this.winH + this.elementHeight;
        const progress = Math.max(0, Math.min(1, relativeScroll / totalDistance));
        const scaleProgress = progress > 0.4 ? (progress - 0.4) / 0.6 : 0;
        const scale = 1 - (scaleProgress * this.scaleAmount);
        this.setScaleTransform(`scale(${scale.toFixed(4)})`);
      }

      const settled = scrollY === this.lastScrollY && this.curr === this.target && !this.isDragging;
      this.lastScrollY = scrollY;

      if (settled) {
        this.raf = null;
        return;
      }

      this.raf = requestAnimationFrame(this.boundAnimate);
    }

    onScroll() {
      this.wake();
    }

    wake() {
      if (this.isIntersecting && !this.raf) this.animate();
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      clearTimeout(this.refreshTimer);
      if (this.observer) this.observer.disconnect();
      if (this.resizer) this.resizer.disconnect();
      this.releaseLazyImages();

      window.removeEventListener('resize', this.onWindowResize);
      window.removeEventListener('scroll', this.onScroll);

      if (!this.track) return;

      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('click', this.onClick, true);
    }
  });
}